import { ShipheroReturnStatusZodSchema } from "@redotech/redo-return-core-common/integrations/returns/shiphero/types";
import * as z from "zod";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { SHIPHERO_EXCHANGE_HOLD_DELAY_DEFAULT_SECONDS } from "@redotech/redo-return-core-common/integrations/returns/shiphero/types";
export const SHIPHERO_EXCHANGE_HOLD_DELAY_MAX_SECONDS = 600;

const statusSettingSchema_ = z.object({
  enabled: z.boolean(),
  status: z.string().optional(),
});
export const statusSettingSchema: z.ZodType<StatusSetting> =
  statusSettingSchema_;
export interface StatusSetting extends z.infer<typeof statusSettingSchema_> {}

const rejectionStatusesSchema = z
  .array(z.string().transform((s) => s.trim().toLowerCase()))
  .transform((arr) => Array.from(new Set(arr)));

export const putShipheroIntegration = {
  name: "putShipheroIntegration" as const,
  input: z
    .object({
      refreshToken: z.string().optional(),
      customerAccountID: z.string().optional(),
      setStatusOnReject: statusSettingSchema.optional(),
      setStatusOnReset: statusSettingSchema.optional(),
      processItemsThroughRedo: z.boolean().optional(),
      commonBins: z.array(z.string()).optional(),
      processableStatuses: z.array(ShipheroReturnStatusZodSchema).optional(),
      rejectionStatuses: rejectionStatusesSchema.optional(),
      trustRmaStatusOnly: z.boolean().optional(),
      // null clears the override back to the default; omitted leaves it unchanged.
      exchangeHoldDelaySeconds: z
        .number()
        .int()
        .min(0)
        .max(SHIPHERO_EXCHANGE_HOLD_DELAY_MAX_SECONDS)
        .nullable()
        .optional(),
    })
    .refine(
      (data) =>
        !data.rejectionStatuses?.some((s) =>
          (data.processableStatuses as string[] | undefined)?.includes(s),
        ),
      {
        message: "Rejection statuses cannot overlap with processable statuses.",
        path: ["rejectionStatuses"],
      },
    ),
  output: z.object({}),
};

export type PutShipheroIntegrationInput = z.infer<
  typeof putShipheroIntegration.input
>;

export const getShipHeroIntegration = {
  name: "getShipHeroIntegration" as const,
  input: z.object({}),
  output: z.object({
    shipHeroIntegration: z.optional(
      z.object({
        customerAccountID: z.string().optional(),
        createReturns: z.boolean(),
        setStatusOnReject: statusSettingSchema.optional(),
        setStatusOnReset: statusSettingSchema.optional(),
        processItemsThroughRedo: z.boolean().optional(),
        commonBins: z.array(z.string()).optional(),
        processableStatuses: z.array(ShipheroReturnStatusZodSchema).optional(),
        rejectionStatuses: z.array(z.string()).optional(),
        trustRmaStatusOnly: z.boolean().optional(),
        exchangeHoldDelaySeconds: z.number().optional(),
      }),
    ),
  }),
};

export type GetShipHeroIntegrationOutput = z.infer<
  typeof getShipHeroIntegration.output
>;

export const disconnectShipHeroIntegration = {
  name: "disconnectShipHeroIntegration" as const,
  input: z.object({}),
  output: z.object({}),
};

export const validateShipHeroLocation = {
  name: "validateShipHeroLocation" as const,
  input: z.object({
    locationName: z.string().min(1, "Location name is required"),
  }),
  output: z.object({ locationId: z.string() }),
};

export type ValidateShipHeroLocationInput = z.infer<
  typeof validateShipHeroLocation.input
>;

export type ValidateShipHeroLocationOutput = z.infer<
  typeof validateShipHeroLocation.output
>;

export const shipHeroInventoryAdd = {
  name: "shipHeroInventoryAdd" as const,
  input: z.object({
    binName: z.string().min(1, "Bin name is required"),
    sku: z.string().min(1, "SKU is required"),
    quantity: z.number().int().positive("Quantity must be a positive integer"),
    reason: z.string().optional(),
  }),
  output: z.object({ warehouseProductId: z.string() }),
};

export type ShipHeroInventoryAddInput = z.infer<
  typeof shipHeroInventoryAdd.input
>;

export type ShipHeroInventoryAddOutput = z.infer<
  typeof shipHeroInventoryAdd.output
>;

const warehouseBinSchema = z.object({ name: z.string(), quantity: z.number() });

export const getShipHeroProductBins = {
  name: "getShipHeroProductBins" as const,
  input: z.object({ skus: z.array(z.string().min(1)).min(1) }),
  output: z.object({
    productBins: z.record(z.string(), z.array(warehouseBinSchema)),
  }),
};

export type GetShipHeroProductBinsInput = z.infer<
  typeof getShipHeroProductBins.input
>;

export type GetShipHeroProductBinsOutput = z.infer<
  typeof getShipHeroProductBins.output
>;

export type WarehouseBin = z.infer<typeof warehouseBinSchema>;
